import React, { useEffect, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sun, MessageSquare, Scale, FolderOpen, Clock, Calendar, Timer, ListTodo, Mail,
  Users, BookUser, Contact as ContactIcon, Building2, Receipt, TrendingUp, BarChart3,
  Wallet, Banknote, UserCheck, FileText, FileSearch, BookTemplate, FolderArchive,
  UsersRound, CalendarClock, Settings, Shield, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Sheet, SheetContent } from '../ui/sheet';
import BrandLogo from '../BrandLogo';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../lib/permissions';
import api from '../../lib/api';

const MGMT = ['administrateur', 'associé', 'associe'];
const COLLAPSE_KEY = 'adalia-rail-collapsed';

/* ───────────────────────────────────────────────────────────────────
   LE REGISTRE — version DEUX NIVEAUX.
   1) un RAIL d'icônes : les grands univers du cabinet ;
   2) un PANNEAU contextuel : la sous-navigation de l'univers actif.
   L'univers actif se déduit de la route (ouvrir /facturation montre « Cabinet »).
   Repli : le panneau se masque, le rail reste. Mémorisé en localStorage.
   ─────────────────────────────────────────────────────────────────── */
const UNIVERS = [
  { id: 'aujourdhui', label: "Aujourd'hui", icon: Sun, items: [
    { path: '/', label: 'Tableau de bord', icon: Sun, exact: true },
    { path: '/chat', label: 'Maître Adalia', icon: MessageSquare },
  ]},
  { id: 'affaires', label: 'Affaires', icon: Scale, items: [
    { path: '/dossiers', label: 'Dossiers', icon: FolderOpen },
    { path: '/echeances', label: 'Échéances', icon: Clock, signal: 'echeances' },
    { path: '/calendrier', label: 'Calendrier', icon: Calendar },
    { path: '/temps', label: 'Suivi du temps', icon: Timer },
    { path: '/taches', label: 'Tâches', icon: ListTodo },
    { path: '/courriers', label: 'Courriers', icon: Mail },
  ]},
  { id: 'clients', label: 'Clients', icon: Users, items: [
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/contacts', label: 'Contacts', icon: BookUser },
    { path: '/intervenants', label: 'Intervenants', icon: ContactIcon },
  ]},
  { id: 'cabinet', label: 'Le cabinet', icon: Building2, roles: MGMT, items: [
    { path: '/facturation', label: 'Facturation', icon: Receipt, signal: 'impayes' },
    { path: '/finance', label: 'Pilotage financier', icon: TrendingUp },
    { path: '/rapports', label: 'Rapports', icon: BarChart3 },
    { path: '/depenses', label: 'Dépenses', icon: Wallet },
    { path: '/personnel', label: 'Personnel', icon: ContactIcon },
    { path: '/paie', label: 'Paie & salaires', icon: Banknote },
    { path: '/portail-admin', label: 'Accès clients', icon: UserCheck },
  ]},
  { id: 'documents', label: 'Documents', icon: FileText, items: [
    // La GED en premier : on y ENTRE (par client, par dossier, par la bannette).
    // La recherche plein texte vient après — elle suppose de savoir quoi chercher.
    { path: '/ged', label: 'Toutes les pièces', icon: FolderOpen },
    { path: '/recherche-documents', label: 'Recherche GED', icon: FileSearch },
    { path: '/contrats', label: 'Contrats', icon: FileText },
    { path: '/modeles', label: 'Modèles OHADA', icon: BookTemplate },
  ]},
];

const REGLAGES = { id: 'reglages', label: 'Réglages', icon: Settings, items: [
  { path: '/equipe', label: 'Équipe', icon: UsersRound },
  { path: '/catalogue-delais', label: 'Catalogue délais', icon: CalendarClock, roles: MGMT },
]};

/* Signaux vivants (échecs silencieux : la nav ne casse jamais). */
function useSignaux(role, isSuper) {
  const [sig, setSig] = useState({});
  useEffect(() => {
    if (isSuper) return;
    let mort = false;
    (async () => {
      try {
        const r = await api.get('/api/echeances/stats');
        if (!mort && r?.data) {
          const { en_retard = 0, cette_semaine = 0 } = r.data;
          if (en_retard > 0) setSig(s => ({ ...s, echeances: { n: en_retard, ton: 'garance' } }));
          else if (cette_semaine > 0) setSig(s => ({ ...s, echeances: { n: cette_semaine, ton: 'ambre' } }));
        }
      } catch { /* silencieux */ }
      if (MGMT.includes(role)) {
        try {
          const r = await api.get('/api/factures', { params: { statut: 'En retard' } });
          const n = Array.isArray(r?.data) ? r.data.length : 0;
          if (!mort && n > 0) setSig(s => ({ ...s, impayes: { n, ton: 'or' } }));
        } catch { /* silencieux */ }
      }
    })();
    return () => { mort = true; };
  }, [role, isSuper]);
  return sig;
}

// Urgence : trois niveaux, mais une SEULE teinte. La distinction vient du
// remplissage (plein / contour), pas de la couleur — c'est la règle du thème
// « Récit ». Trois teintes vives feraient de la barre latérale un vitrail, et
// la plus grave n'y serait plus la plus visible.
const TONS = {
  garance: { bg: 'var(--mlx-danger)', fg: 'var(--bg-secondary)', dot: 'var(--mlx-danger)' },
  ambre:   { bg: 'transparent', fg: 'var(--mlx-danger)', dot: 'var(--mlx-danger)',
             bord: 'var(--mlx-danger)' },
  or:      { bg: 'transparent', fg: 'var(--text-secondary)', dot: 'var(--text-tertiary)',
             bord: 'var(--border-soft)' },
};

function Badge({ s }) {
  if (!s) return null;
  const t = TONS[s.ton] || TONS.or;
  // Le contour porte la distinction quand le fond est transparent.
  return <span className="ml-auto text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full"
               style={{ background: t.bg, color: t.fg,
                        border: t.bord ? `1px solid ${t.bord}` : 'none' }}>{s.n}</span>;
}

/* ── Un item du panneau (NavLink) ── */
function ItemLien({ item, sig, onLinkClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.exact}
      onClick={onLinkClick}
      data-testid={`sidebar-nav-item-${item.label.toLowerCase().replace(/[\s’é]/g, '-')}`}
      className="relative flex items-center gap-3 px-3 py-2 rounded-[9px] text-[13px] transition-all duration-200 group"
      style={({ isActive }) => isActive
        ? { background: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-fg)', fontWeight: 700 }
        : { color: 'var(--sidebar-muted)', fontWeight: 500 }}
    >
      {({ isActive }) => (
        <>
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[2.5px] rounded-full transition-all duration-300"
                style={{ height: isActive ? '60%' : '0%', background: 'var(--sidebar-rail)' }} aria-hidden />
          <Icon className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                style={isActive ? { color: 'var(--sidebar-active-icon)' } : undefined} />
          <span className="truncate">{item.label}</span>
          <Badge s={sig[item.signal]} />
        </>
      )}
    </NavLink>
  );
}

/* ── Le rail d'icônes (les univers) ── */
function Rail({ univers, activeId, onPick, reglages, isSuper }) {
  const Btn = ({ u, active, onClick, dot }) => {
    const Icon = u.icon;
    return (
      <button
        type="button" onClick={onClick} title={u.label}
        data-testid={`rail-${u.id}`}
        className="relative w-10 h-10 rounded-[10px] flex items-center justify-center transition-colors duration-200"
        style={active
          ? { background: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-icon)' }
          : { color: 'var(--sidebar-muted)' }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--sidebar-hover)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon className="w-[18px] h-[18px]" />
        {dot && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: dot }} aria-hidden />}
      </button>
    );
  };
  return (
    <div className="w-[60px] flex-shrink-0 h-full flex flex-col items-center py-3 gap-1.5"
         style={{ background: 'var(--sidebar-rail-bg)', borderRight: '1px solid var(--sidebar-border)' }}>
      <div className="mb-2"><BrandLogo src="/branding/adalia-icon.png?v=2" height={30} fallbackVariant="mark" /></div>
      {univers.map(u => <Btn key={u.id} u={u} active={u.id === activeId} onClick={() => onPick(u.id)} dot={u.dot} />)}
      <div className="mt-auto flex flex-col items-center gap-1.5">
        {!isSuper && <Btn u={reglages} active={reglages.id === activeId} onClick={() => onPick(reglages.id)} />}
      </div>
    </div>
  );
}

/* ── Le panneau contextuel ── */
function Panneau({ univers, sig, collapsed, onToggle, onLinkClick }) {
  if (!univers) return null;
  return (
    <div className="h-full flex flex-col overflow-hidden transition-all duration-300"
         style={{ width: collapsed ? 0 : 224, opacity: collapsed ? 0 : 1 }}>
      <div className="h-[64px] flex items-center justify-between px-3 border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
        {/* Jeton et non couleur en dur : ce libellé était figé à #FAFAFA, donc
            invisible dès que la barre latérale cesse d'être sombre. */}
        <div className="font-serif font-medium text-[18px] truncate pl-1"
             style={{ color: 'var(--sidebar-active-fg)' }}>
          {univers.label}
        </div>
        {onToggle && (
          <button type="button" onClick={onToggle} title="Replier"
                  data-testid="sidebar-collapse-button"
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                  style={{ color: 'var(--sidebar-title)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5" style={{ scrollbarWidth: 'thin' }}>
        {univers.items.map(it => <ItemLien key={it.path} item={it} sig={sig} onLinkClick={onLinkClick} />)}
      </nav>
    </div>
  );
}

/* ── Contenu mobile : liste plate par univers (Sheet) ── */
function MobileFlat({ univers, sig, onLinkClick }) {
  return (
    <>
      <div className="h-[64px] flex items-center px-4 border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
        <BrandLogo src="/branding/adalia-logo.png?v=2" height={36} fallbackVariant="full" />
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {univers.map(u => (
          <div key={u.id} className="mt-3 first:mt-0">
            <div className="flex items-center gap-2 px-3 mb-1.5">
              <span className="text-[9.5px] font-semibold uppercase" style={{ letterSpacing: '0.2em', color: 'var(--sidebar-title)' }}>{u.label}</span>
              <span className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, var(--sidebar-filet), transparent)' }} />
            </div>
            <div className="space-y-0.5">
              {u.items.map(it => <ItemLien key={it.path} item={it} sig={sig} onLinkClick={onLinkClick} />)}
            </div>
          </div>
        ))}
      </nav>
    </>
  );
}

function useStructure(role, isSuper) {
  return useMemo(() => {
    if (isSuper) {
      return { univers: [{ id: 'plateforme', label: 'Plateforme', icon: Shield, items: [
        { path: '/super-admin', label: 'Plateforme', icon: Shield },
      ]}], reglages: { id: 'reglages', label: 'Réglages', icon: Settings, items: [] } };
    }
    const filtre = (u) => ({ ...u, items: u.items.filter(it => !it.roles || it.roles.includes(role)) });
    const univers = UNIVERS.filter(u => !u.roles || u.roles.includes(role)).map(filtre).filter(u => u.items.length);
    const reglages = filtre(REGLAGES);
    return { univers, reglages };
  }, [role, isSuper]);
}

function activeUniverseId(structure, pathname) {
  const all = [...structure.univers, structure.reglages];
  let best = null, bestLen = -1;
  for (const u of all) {
    for (const it of (u.items || [])) {
      const match = it.exact ? pathname === it.path : (pathname === it.path || pathname.startsWith(it.path + '/') || pathname.startsWith(it.path));
      if (match && it.path.length > bestLen) { best = u.id; bestLen = it.path.length; }
    }
  }
  return best || structure.univers[0]?.id;
}

export default function Sidebar({ collapsed, onToggle, mobile = false, isOpen = false, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const isSuper = isSuperAdmin(user?.role, user?.is_super_admin);
  const role = (user?.role || '').toLowerCase();
  const sig = useSignaux(role, isSuper);
  const structure = useStructure(role, isSuper);

  // Univers actif : déduit de la route, ré-ajustable au clic sur le rail.
  const routeUid = activeUniverseId(structure, location.pathname);
  const [activeId, setActiveId] = useState(routeUid);
  useEffect(() => { setActiveId(routeUid); }, [routeUid]);

  // Pastilles sur le rail (un univers porte un point s'il a un signal).
  const univAvecDot = structure.univers.map(u => ({
    ...u,
    dot: u.items.some(it => sig[it.signal]) ? (TONS[(sig[u.items.find(it => sig[it.signal])?.signal] || {}).ton] || TONS.or).dot : null,
  }));
  const activeUniv = [...univAvecDot, structure.reglages].find(u => u.id === activeId) || univAvecDot[0];

  const pickUniverse = (id) => { setActiveId(id); if (collapsed && onToggle) onToggle(); };

  if (mobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="left" className="w-[260px] p-0" style={{ background: 'var(--sidebar-bg)', border: 'none' }}>
          <div className="h-full flex flex-col">
            <MobileFlat univers={isSuper ? structure.univers : [...structure.univers, structure.reglages]} sig={sig} onLinkClick={onClose} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className="fixed left-0 top-0 h-screen z-40 flex"
           style={{ background: 'var(--sidebar-bg)' }}>
      <Rail univers={univAvecDot} activeId={activeId} onPick={pickUniverse} reglages={structure.reglages} isSuper={isSuper} />
      <Panneau univers={activeUniv} sig={sig} collapsed={collapsed} onToggle={onToggle} />
      {/* Poignée pour déplier quand le panneau est replié */}
      {collapsed && onToggle && (
        <button type="button" onClick={onToggle} title="Déplier"
                data-testid="sidebar-expand-button"
                className="absolute left-[52px] top-1/2 -translate-y-1/2 w-5 h-12 rounded-r-lg flex items-center justify-center transition-colors"
                style={{ background: 'var(--sidebar-rail-bg)', color: 'var(--sidebar-title)', borderTop: '1px solid var(--sidebar-border)', borderRight: '1px solid var(--sidebar-border)', borderBottom: '1px solid var(--sidebar-border)' }}>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </aside>
  );
}
